import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CdkDrag,
  CdkDropList,
  CdkDropListGroup,
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrderService } from '../../../core/services/order.service';
import { AlertService } from '../../../core/services/alert.service';
import { OrdersGroupsResponse, TEMPORARY_GROUP_ID } from '../../../core/models/order.model';

/** A draggable order within a group box. Carries its destination fingerprint
 * so a box only accepts orders bound for the same address. */
interface OrderChip {
  id: string;
  addressMd5: string;
}

/** Local editable view of a group. addressMd5 is derived from the first order;
 * an empty box has no fingerprint yet and accepts any order. */
interface LocalGroup {
  groupId: string;
  enabled: boolean;
  shippingDate: string;
  orders: OrderChip[];
}

/**
 * "Agrupar Envios" — lets a customer group their pending orders that ship to
 * the same address so they go out together. Orders are dragged between boxes
 * (CDK drag-drop); a box only accepts orders sharing its addressMd5. The full
 * desired set is saved via POST /api/orders/groups and the response repaints
 * the UI. Only reachable when user.allowOrdersGrouping is true (gated at the
 * call site in the topbar menu).
 */
@Component({
  selector: 'app-order-grouping-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    TranslateModule
  ],
  template: `
    <div class="grouping-dialog">
      <div class="dialog-header">
        <h2>{{ 'ORDER_GROUPING.TITLE' | translate }}</h2>
        <button type="button" class="close-btn" (click)="close()" [attr.aria-label]="'COMMON.CLOSE' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="dialog-body">
        @if (loading()) {
          <div class="state-block">
            <mat-spinner diameter="36"></mat-spinner>
          </div>
        } @else if (unavailable()) {
          <div class="state-block">
            <mat-icon>block</mat-icon>
            <p>{{ 'ORDER_GROUPING.UNAVAILABLE' | translate }}</p>
          </div>
        } @else if (errorStatus()) {
          <div class="state-block error">
            <mat-icon>error_outline</mat-icon>
            <p>{{ 'ORDER_GROUPING.ERROR_TRY_LATER' | translate }}</p>
            <button mat-stroked-button (click)="loadGroups()">
              <mat-icon>refresh</mat-icon>
              {{ 'COMMON.RETRY' | translate }}
            </button>
          </div>
        } @else if (groups().length === 0) {
          <div class="state-block">
            <mat-icon>inbox</mat-icon>
            <p>{{ 'ORDER_GROUPING.EMPTY' | translate }}</p>
          </div>
        } @else {
          <div class="note">
            <mat-icon>info</mat-icon>
            <span>{{ 'ORDER_GROUPING.NOTE' | translate }}</span>
          </div>

          <div class="groups" cdkDropListGroup>
            @for (group of groups(); track group.groupId + '_' + $index; let i = $index) {
              <div class="group-box" [class.disabled]="!group.enabled">
                <div class="group-head">
                  <span class="group-label">{{ 'ORDER_GROUPING.SHIP_DATE_LABEL' | translate }}</span>
                  @if (!group.enabled) {
                    <!-- enabled is owned by the backend; shown read-only here -->
                    <mat-icon class="group-lock" [matTooltip]="'ORDER_GROUPING.BLOCKED' | translate">lock</mat-icon>
                  }
                  <span class="group-date">{{ formatShipDate(group.shippingDate) }}</span>
                </div>

                <div
                  class="drop-zone"
                  cdkDropList
                  [cdkDropListData]="group"
                  [cdkDropListEnterPredicate]="enterPredicate"
                  (cdkDropListDropped)="drop($event)">
                  @for (order of group.orders; track order.id) {
                    <div class="order-chip" cdkDrag [cdkDragData]="order" [cdkDragDisabled]="!group.enabled">
                      <span class="order-id">{{ order.id }}</span>
                      <mat-icon cdkDragHandle class="drag-handle">open_with</mat-icon>
                    </div>
                  }
                  @if (group.orders.length === 0) {
                    <div class="drop-placeholder">{{ 'ORDER_GROUPING.DROP_HERE' | translate }}</div>
                  }
                </div>

                <div class="group-bar" [class.on]="group.enabled"></div>
              </div>
            }
          </div>

          <div class="add-group">
            <button mat-raised-button color="primary" (click)="addGroup()">
              <mat-icon>add</mat-icon>
              {{ 'ORDER_GROUPING.NEW_GROUP' | translate }}
            </button>
          </div>
        }
      </div>

      <div class="dialog-footer">
        <button mat-button (click)="close()" [disabled]="saving()">{{ 'ORDER_GROUPING.CLOSE' | translate }}</button>
        <button mat-button color="primary" (click)="save()" [disabled]="saving() || loading() || !canSave()">
          @if (saving()) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            {{ 'ORDER_GROUPING.SAVE' | translate }}
          }
        </button>
      </div>
    </div>
  `,
  styleUrl: './order-grouping-dialog.component.scss'
})
export class OrderGroupingDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<OrderGroupingDialogComponent>);
  private readonly orderService = inject(OrderService);
  private readonly alert = inject(AlertService);
  private readonly translate = inject(TranslateService);

  readonly groups = signal<LocalGroup[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly unavailable = signal(false);
  readonly errorStatus = signal<string | null>(null);

  ngOnInit(): void {
    this.loadGroups();
  }

  loadGroups(): void {
    this.loading.set(true);
    this.unavailable.set(false);
    this.errorStatus.set(null);
    this.orderService.getOrdersGroups().subscribe(res => {
      this.loading.set(false);
      this.applyResponse(res);
    });
  }

  /** Map a GET/POST response into local state, or set the unavailable/error
   * flags for a status object. */
  private applyResponse(res: OrdersGroupsResponse): void {
    if (Array.isArray(res)) {
      this.groups.set(res.map(g => ({
        groupId: g.groupId,
        enabled: g.enabled !== false,
        shippingDate: g.shippingDate,
        orders: (g.orders || []).map(id => ({ id: String(id), addressMd5: g.addressMd5 }))
      })));
      return;
    }
    if (res?.status === 'UNAVAILABLE') {
      this.unavailable.set(true);
    } else {
      this.errorStatus.set(res?.status || 'ERROR_WS_CONNECTION');
    }
  }

  /** A blocked group rejects drops outright (silent — it's visibly locked).
   * Address-mismatch drops are still allowed to ENTER so drop() can explain
   * with a warning why they can't be grouped, instead of silently refusing.
   * Dragging OUT of a blocked group is prevented via cdkDragDisabled. */
  enterPredicate = (_drag: CdkDrag<OrderChip>, drop: CdkDropList<LocalGroup>): boolean => {
    return !!drop.data?.enabled;
  };

  drop(event: CdkDragDrop<LocalGroup>): void {
    const prev = event.previousContainer.data;
    const curr = event.container.data;

    if (prev === curr) {
      // Pure reorder — membership unchanged, ship date stays.
      moveItemInArray(curr.orders, event.previousIndex, event.currentIndex);
      this.groups.set([...this.groups()]);
      return;
    }

    // Only same-destination orders may be grouped. Reject a cross-address drop
    // with an explanatory warning and leave the order where it was.
    const item = event.item.data as OrderChip;
    if (curr.orders.length > 0 && curr.orders[0].addressMd5 !== item.addressMd5) {
      this.alert.warning(this.translate.instant('ORDER_GROUPING.DIFFERENT_ADDRESS'));
      return;
    }

    transferArrayItem(prev.orders, curr.orders, event.previousIndex, event.currentIndex);

    // Both boxes changed membership, so their estimated ship dates are now
    // stale — the server recomputes them on save. Reset them in the view
    // (renders "—") until the saved response brings the real dates back.
    prev.shippingDate = 'UNKNOWN';
    curr.shippingDate = 'UNKNOWN';
    this.groups.set([...this.groups()]);
  }

  addGroup(): void {
    this.groups.set([
      ...this.groups(),
      { groupId: TEMPORARY_GROUP_ID, enabled: true, shippingDate: 'UNKNOWN', orders: [] }
    ]);
  }

  /** Something is worth saving only once at least one box holds an order. */
  canSave(): boolean {
    return this.groups().some(g => g.orders.length > 0);
  }

  formatShipDate(date: string): string {
    return !date || date === 'UNKNOWN' ? '—' : date;
  }

  save(): void {
    // Full replace: send every non-empty group, keeping its groupId (or
    // TEMPORARY_GROUP for new ones) and carrying through the addressMd5 of its
    // orders. Empty boxes are dropped.
    const payload = this.groups()
      .filter(g => g.orders.length > 0)
      .map(g => ({
        groupId: g.groupId,
        orders: g.orders.map(o => o.id),
        enabled: g.enabled,
        addressMd5: g.orders[0].addressMd5
      }));

    if (payload.length === 0) {
      this.close();
      return;
    }

    this.saving.set(true);
    this.orderService.setOrdersGroups(payload).subscribe(res => {
      this.saving.set(false);
      if (Array.isArray(res)) {
        this.applyResponse(res);
        this.alert.success(this.translate.instant('ORDER_GROUPING.SAVE_SUCCESS'));
      } else if (res?.status === 'UNAVAILABLE') {
        this.unavailable.set(true);
      } else {
        // ERROR_WS_CONNECTION or a PHC rejection message — keep local state.
        this.alert.error(
          res?.status === 'ERROR_WS_CONNECTION'
            ? this.translate.instant('ORDER_GROUPING.ERROR_TRY_LATER')
            : (res?.status || this.translate.instant('ORDER_GROUPING.SAVE_ERROR'))
        );
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
